/**
 * Client-side validation for the patch-campaign create form and the execute dialog.
 *
 * This mirrors the server rules in docs/patch-management/03-interface-contract.md
 * (§2 enums, §3 explicit targets, change ticket required for prod, §4 maintenance
 * window) so users get fast feedback. It is a convenience only: the portal
 * re-validates everything and its field errors are displayed too.
 */
import {
  LINUX_CLASSIFICATIONS,
  PATCH_ENVIRONMENTS,
  REBOOT_SETTINGS,
  WINDOWS_CLASSIFICATIONS,
  type CreatePatchCampaignRequest,
  type LinuxClassification,
  type MaintenanceWindow,
  type PatchEnvironment,
  type PatchScope,
  type PatchTargetRequest,
  type PatchTargets,
  isTagTarget,
  type RebootSetting,
  type WindowsClassification,
} from '@/types/patching'

/** Field path (dotted, same naming as the API) → messages. */
export type FieldErrors = Record<string, string[]>

export const NAME_MAX = 200
export const DESCRIPTION_MAX = 2000
export const CHANGE_TICKET_MAX = 100

const CAMPAIGN_ID_RE = /^PCH-\d{4}-\d{4,}$/
const PRODUCT_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/
const APPLICATION_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/
const KB_RE = /^KB\d{4,10}$/
/** Package-name masks may use `*` as the wildcard. */
const MASK_RE = /^[A-Za-z0-9][A-Za-z0-9._+:~*-]*$/
const VM_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/
const RESOURCE_GROUP_RE = /^[A-Za-z0-9_.()-]{1,90}$/
const WILDCARD_RE = /[*?[\]%]/
const WHITESPACE_RE = /\s/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isValidCampaignId(id: unknown): id is string {
  return typeof id === 'string' && CAMPAIGN_ID_RE.test(id)
}

export function isPatchEnvironment(value: unknown): value is PatchEnvironment {
  return typeof value === 'string' && (PATCH_ENVIRONMENTS as readonly string[]).includes(value)
}

export function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some((messages) => messages.length > 0)
}

function addError(errors: FieldErrors, path: string, message: string): void {
  const list = errors[path] ?? []
  list.push(message)
  errors[path] = list
}

// ─── Normalisation (contract §2, applied before submit) ─────────────────────

/** Strip whitespace, drop empties, de-duplicate (case-insensitive), keep first-seen order. */
export function normalizeEntries(values: readonly string[], upperCase = false): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of values) {
    const value = raw.trim()
    if (!value) continue
    const next = upperCase ? value.toUpperCase() : value
    const key = next.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(next)
  }
  return out
}

// ─── Targets (contract §3) ───────────────────────────────────────────────────

export function validateVmNames(names: readonly string[]): string[] {
  const messages: string[] = []
  if (names.length === 0) {
    messages.push('Add at least one VM name.')
    return messages
  }
  const seen = new Map<string, string>()
  names.forEach((name, index) => {
    const label = `VM name #${index + 1}`
    if (name.trim() === '') {
      messages.push(`${label} is empty.`)
      return
    }
    if (WHITESPACE_RE.test(name)) {
      messages.push(`${label} ("${name.trim()}") must not contain whitespace.`)
      return
    }
    if (WILDCARD_RE.test(name)) {
      messages.push(`${label} ("${name}") must be an explicit VM name — wildcards are not allowed.`)
      return
    }
    if (!VM_NAME_RE.test(name)) {
      messages.push(
        `${label} ("${name}") is not a valid VM name (letters, digits, '.', '_' and '-' only, up to 64 characters).`,
      )
      return
    }
    const key = name.toLowerCase()
    if (seen.has(key)) {
      messages.push(`Duplicate VM name "${name}".`)
    } else {
      seen.set(key, name)
    }
  })
  return messages
}

export function validateResourceGroup(resourceGroup: string): string[] {
  if (resourceGroup.trim() === '') return ['Resource group is required.']
  if (WHITESPACE_RE.test(resourceGroup)) return ['Resource group must not contain whitespace.']
  if (WILDCARD_RE.test(resourceGroup)) {
    return ['Resource group must be an explicit name — wildcards are not allowed.']
  }
  if (!RESOURCE_GROUP_RE.test(resourceGroup) || resourceGroup.endsWith('.')) {
    return ["Resource group may contain letters, digits, '_', '.', '(', ')' and '-' (not ending in '.')."]
  }
  return []
}

export const TAG_NAME_MIN = 3
export const TAG_NAME_MAX = 50
export const TAG_DESCRIPTION_MAX = 500

/** Why `name` cannot be a tag name (the server applies the same rules). A tag name is exact: never a pattern. */
export function validateTagName(name: string): string[] {
  if (name === '') return ['A tag name is required.']
  if (name.length < TAG_NAME_MIN || name.length > TAG_NAME_MAX) return [`A tag name must be ${TAG_NAME_MIN} to ${TAG_NAME_MAX} characters.`]
  if (/\s/.test(name)) return ['A tag name cannot contain spaces or other whitespace.']
  if (/[*?%[\]]/.test(name)) return ['A tag name is an exact name: wildcards are not allowed.']
  if (name !== name.toLowerCase()) return ['A tag name uses lowercase letters only.']
  if (!/^[a-z0-9_-]+$/.test(name)) return ['A tag name may contain only lowercase letters, digits, hyphens and underscores.']
  return []
}

function validateTarget(env: PatchEnvironment, target: PatchTargetRequest | undefined, errors: FieldErrors): void {
  if (!target) {
    if (env === 'dev') addError(errors, 'environments.dev', 'A DEV target is required.')
    return
  }
  if (isTagTarget(target)) {
    for (const message of validateTagName(target.tag_name)) addError(errors, `environments.${env}.tag_name`, message)
    return
  }
  for (const message of validateResourceGroup(target.resource_group)) {
    addError(errors, `environments.${env}.resource_group`, message)
  }
  for (const message of validateVmNames(target.vm_names)) {
    addError(errors, `environments.${env}.vm_names`, message)
  }
}

// ─── Scope (contract §2) ─────────────────────────────────────────────────────

function checkEnum<T extends string>(
  errors: FieldErrors,
  path: string,
  values: readonly string[],
  allowed: readonly T[],
): void {
  for (const value of values) {
    if (!(allowed as readonly string[]).includes(value)) {
      addError(errors, path, `"${value}" is not an allowed value (${allowed.join(', ')}).`)
    }
  }
}

function checkEntries(errors: FieldErrors, path: string, values: readonly string[], re: RegExp, hint: string): void {
  for (const value of values) {
    if (!re.test(value)) addError(errors, path, `"${value}" is invalid — ${hint}`)
  }
}

function checkOverlap(errors: FieldErrors, path: string, include: readonly string[], exclude: readonly string[]): void {
  const excluded = new Set(exclude.map((v) => v.toLowerCase()))
  for (const value of include) {
    if (excluded.has(value.toLowerCase())) {
      addError(errors, path, `"${value}" is both included and excluded.`)
    }
  }
}

function isRealDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false
  const [y, m, d] = value.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

export function validateScope(scope: PatchScope): FieldErrors {
  const errors: FieldErrors = {}
  const windows = scope.classifications?.windows ?? []
  const linux = scope.classifications?.linux ?? []
  checkEnum(errors, 'patch_scope.classifications.windows', windows, WINDOWS_CLASSIFICATIONS)
  checkEnum(errors, 'patch_scope.classifications.linux', linux, LINUX_CLASSIFICATIONS)

  const kbInclude = normalizeEntries(scope.kb_numbers_to_include, true)
  const kbExclude = normalizeEntries(scope.kb_numbers_to_exclude, true)
  if (windows.length + linux.length === 0 && kbInclude.length === 0) {
    addError(errors, 'patch_scope.classifications', 'Select at least one classification (or include specific KB numbers).')
  }
  checkEntries(errors, 'patch_scope.kb_numbers_to_include', kbInclude, KB_RE, 'expected a KB number such as KB5000001.')
  checkEntries(errors, 'patch_scope.kb_numbers_to_exclude', kbExclude, KB_RE, 'expected a KB number such as KB5000001.')
  checkOverlap(errors, 'patch_scope.kb_numbers_to_exclude', kbInclude, kbExclude)

  const maskInclude = normalizeEntries(scope.package_name_masks_to_include)
  const maskExclude = normalizeEntries(scope.package_name_masks_to_exclude)
  const maskHint = "use letters, digits, '.', '_', '+', ':', '~', '-' and '*' as the wildcard."
  checkEntries(errors, 'patch_scope.package_name_masks_to_include', maskInclude, MASK_RE, maskHint)
  checkEntries(errors, 'patch_scope.package_name_masks_to_exclude', maskExclude, MASK_RE, maskHint)
  checkOverlap(errors, 'patch_scope.package_name_masks_to_exclude', maskInclude, maskExclude)

  if (scope.max_patch_publish_date !== null && !isRealDate(scope.max_patch_publish_date)) {
    addError(errors, 'patch_scope.max_patch_publish_date', 'Use a valid date in YYYY-MM-DD format.')
  }
  if (!(REBOOT_SETTINGS as readonly string[]).includes(scope.reboot_setting)) {
    addError(errors, 'patch_scope.reboot_setting', `Must be one of ${REBOOT_SETTINGS.join(', ')}.`)
  }
  checkEntries(
    errors,
    'patch_scope.applications',
    normalizeEntries(scope.applications),
    APPLICATION_RE,
    "use a catalogue name such as 'ross-erp' (letters, digits, '.', '_', '-').",
  )
  return errors
}

// ─── Whole request ───────────────────────────────────────────────────────────

/** A target is "provided" when its key is present in `environments`. */
export function isProdTargeted(environments: Partial<PatchTargets>): boolean {
  return environments.prod !== undefined
}

export function validateCampaignRequest(request: CreatePatchCampaignRequest): FieldErrors {
  const errors: FieldErrors = {}

  const name = request.name.trim()
  if (!name) addError(errors, 'name', 'Name is required.')
  else if (name.length > NAME_MAX) addError(errors, 'name', `Name must be at most ${NAME_MAX} characters.`)
  if (request.description.length > DESCRIPTION_MAX) {
    addError(errors, 'description', `Description must be at most ${DESCRIPTION_MAX} characters.`)
  }

  const product = request.product.trim()
  if (!product) addError(errors, 'product', 'Product is required.')
  else if (!PRODUCT_RE.test(product)) {
    addError(errors, 'product', "Product may contain letters, digits, '.', '_' and '-' only.")
  }

  const ticket = request.change_ticket.trim()
  if (isProdTargeted(request.environments) && !ticket) {
    addError(errors, 'change_ticket', 'A change ticket is required when a PROD target is provided.')
  }
  if (ticket.length > CHANGE_TICKET_MAX) {
    addError(errors, 'change_ticket', `Change ticket must be at most ${CHANGE_TICKET_MAX} characters.`)
  }

  for (const env of PATCH_ENVIRONMENTS) {
    validateTarget(env, request.environments[env], errors)
  }
  return { ...errors, ...validateScope(request.patch_scope) }
}

// ─── Form model → request ────────────────────────────────────────────────────

/** How an environment's VMs are chosen: listed one by one, or by the name of a tag. */
export type TargetMode = 'explicit' | 'tag'

export interface TargetFormValues {
  included: boolean
  mode: TargetMode
  resource_group: string
  /** One entry per input row (raw, so blanks/whitespace are validated, not hidden). */
  vm_names: string[]
  /** Only used in tag mode. */
  tag_name: string
}

export interface CampaignFormValues {
  name: string
  description: string
  product: string
  change_ticket: string
  windows: WindowsClassification[]
  linux: LinuxClassification[]
  kb_include: string[]
  kb_exclude: string[]
  mask_include: string[]
  mask_exclude: string[]
  /** `YYYY-MM-DD` or empty. */
  max_patch_publish_date: string
  reboot_setting: RebootSetting
  applications: string[]
  environments: Record<PatchEnvironment, TargetFormValues>
}

export function defaultFormValues(): CampaignFormValues {
  return {
    name: '',
    description: '',
    product: '',
    change_ticket: '',
    windows: ['Critical', 'Security'],
    linux: ['Critical', 'Security'],
    kb_include: [],
    kb_exclude: [],
    mask_include: [],
    mask_exclude: [],
    max_patch_publish_date: '',
    reboot_setting: 'IfRequired',
    applications: [],
    environments: {
      dev: { included: true, mode: 'explicit', resource_group: '', vm_names: [''], tag_name: '' },
      uat: { included: false, mode: 'explicit', resource_group: '', vm_names: [''], tag_name: '' },
      prod: { included: false, mode: 'explicit', resource_group: '', vm_names: [''], tag_name: '' },
    },
  }
}

function targetRequest(t: TargetFormValues): PatchTargetRequest {
  return t.mode === 'tag'
    ? { target_mode: 'tag', tag_name: t.tag_name.trim() }
    : { resource_group: t.resource_group, vm_names: [...t.vm_names] }
}

export function buildCampaignRequest(values: CampaignFormValues): CreatePatchCampaignRequest {
  const environments: PatchTargets = { dev: targetRequest(values.environments.dev) }
  for (const env of ['uat', 'prod'] as const) {
    const t = values.environments[env]
    if (t.included) environments[env] = targetRequest(t)
  }
  return {
    name: values.name.trim(),
    description: values.description.trim(),
    product: values.product.trim(),
    change_ticket: values.change_ticket.trim(),
    patch_scope: {
      schema_version: 1,
      product: values.product.trim(),
      classifications: { windows: [...values.windows], linux: [...values.linux] },
      kb_numbers_to_include: normalizeEntries(values.kb_include, true),
      kb_numbers_to_exclude: normalizeEntries(values.kb_exclude, true),
      package_name_masks_to_include: normalizeEntries(values.mask_include),
      package_name_masks_to_exclude: normalizeEntries(values.mask_exclude),
      max_patch_publish_date: values.max_patch_publish_date.trim() || null,
      reboot_setting: values.reboot_setting,
      applications: normalizeEntries(values.applications),
    },
    environments,
  }
}

// ─── Typed-but-not-added entries ─────────────────────────────────────────────

export type DraftKey = 'kb_include' | 'kb_exclude' | 'mask_include' | 'mask_exclude' | 'applications'

/** What the user has typed into a multi-entry input but not yet committed with Add / Enter. */
export type ScopeDrafts = Record<DraftKey, string>

export const DRAFT_FIELDS: Record<DraftKey, { path: string; label: string }> = {
  kb_include: { path: 'patch_scope.kb_numbers_to_include', label: 'KB numbers to include' },
  kb_exclude: { path: 'patch_scope.kb_numbers_to_exclude', label: 'KB numbers to exclude' },
  mask_include: { path: 'patch_scope.package_name_masks_to_include', label: 'Package name masks to include' },
  mask_exclude: { path: 'patch_scope.package_name_masks_to_exclude', label: 'Package name masks to exclude' },
  applications: { path: 'patch_scope.applications', label: 'Applications' },
}

export function emptyDrafts(): ScopeDrafts {
  return { kb_include: '', kb_exclude: '', mask_include: '', mask_exclude: '', applications: '' }
}

/**
 * A value typed but never added would be silently left out of the scope — an exclusion that is
 * not sent changes what gets patched. Refuse to submit until it is added or cleared, so the scope
 * (and therefore the scope hash) contains exactly the committed values.
 */
export function validatePendingDrafts(drafts: ScopeDrafts): FieldErrors {
  const errors: FieldErrors = {}
  for (const key of Object.keys(DRAFT_FIELDS) as DraftKey[]) {
    const text = drafts[key].trim()
    if (text) {
      addError(
        errors,
        DRAFT_FIELDS[key].path,
        `You typed "${text}" in "${DRAFT_FIELDS[key].label}" but did not add it. Press Add (or Enter) to include it, or clear the field.`,
      )
    }
  }
  return errors
}

// ─── Maintenance window (set by the approver for prod, contract §11.4) ───────

export const MAX_WINDOW_MINUTES = 235
export const MAX_WINDOW_START_DAYS = 14

export type MaintenanceWindowResult = { window: MaintenanceWindow; error: null } | { window: null; error: string }

/** RFC 3339 UTC without fractional seconds, e.g. 2026-10-13T02:00:00Z. */
function toRfc3339Utc(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Convert the two `datetime-local` inputs (browser local time) into the UTC window the contract
 * expects. Rules mirror §11.4: end after start, at most 235 minutes, end in the future, start
 * within 14 days. `now` is injectable for tests.
 */
export function buildMaintenanceWindow(
  startLocal: string,
  endLocal: string,
  now: number = Date.now(),
): MaintenanceWindowResult {
  if (!startLocal || !endLocal) {
    return { window: null, error: 'Maintenance window start and end are required for PROD.' }
  }
  const start = new Date(startLocal)
  const end = new Date(endLocal)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { window: null, error: 'Maintenance window start and end must be valid dates.' }
  }
  if (end.getTime() <= start.getTime()) {
    return { window: null, error: 'Maintenance window end must be after its start.' }
  }
  if (end.getTime() - start.getTime() > MAX_WINDOW_MINUTES * 60_000) {
    return { window: null, error: `The maintenance window may be at most ${MAX_WINDOW_MINUTES} minutes long.` }
  }
  if (end.getTime() <= now) {
    return { window: null, error: 'The maintenance window has already ended.' }
  }
  if (start.getTime() > now + MAX_WINDOW_START_DAYS * 24 * 3_600_000) {
    return { window: null, error: `The maintenance window must start within ${MAX_WINDOW_START_DAYS} days.` }
  }
  return { window: { start: toRfc3339Utc(start), end: toRfc3339Utc(end) }, error: null }
}
